// client/src/components/common/PickerPopover.tsx
//
// The popup the shared date/time pickers open on a desktop (ST-00294): the phone sheet's panel
// (PickerSheet, same props — title, Cancel / Apply, Clear beside the title) anchored to the field
// that opened it. It opens just below the field, or above it when there is not enough room
// below; when it fits on neither side it keeps its whole height and slides over the field to fit
// the window (nothing to scroll inside a picker on a laptop screen). It lines up with the field's
// left edge but stays 16 px inside the window; only a popup taller than the whole window is
// capped — the body scrolls and the Cancel / Apply footer stays in view.
// It follows the field when the window is resized or anything around the field scrolls. The
// page is not scroll-locked (hiding the scrollbar would shift the page): the mouse wheel over
// the rest of the page still scrolls the field's scrolling container.
//
// A labelled modal dialog: a click anywhere outside it, Esc, the close button and Cancel all
// close it without changing anything; Tab stays inside; focus goes to `initialFocusRef` (a text
// field gets its text selected, ready to type over) and returns to the field on close. Esc is
// taken on `window` in the capture phase (useEscapeKey), so a picker inside a dialog (the
// LexiStar Modal, Email Reports, monitor settings) closes itself, not the dialog. It sits above
// every app layer, dialogs included. Motion is dropped under prefers-reduced-motion
// (PickerPopover.css).

import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  type SyntheticEvent,
  type WheelEvent as ReactWheelEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { focusableIn, keepTabInside, type PickerSheetProps } from './PickerSheet';
import { useEscapeKey, useLatest } from './pickerHooks';
import './PickerSheet.css';
import './PickerPopover.css';

/** Space kept between the popup and the window's edges. */
export const POPOVER_GUTTER = 16;
/** Space between the field and the popup. */
export const POPOVER_GAP = 4;

export type PickerPopoverProps = PickerSheetProps;

export interface PopoverPlacement {
  top: number;
  left: number;
  /** The tallest the popup may be here (its body scrolls past it). */
  maxHeight: number;
  side: 'below' | 'above';
}

interface Box {
  top: number;
  bottom: number;
  left: number;
}

/**
 * Where the popup goes, from the field's box, the popup's natural size and the window's size.
 * Below the field (4 px gap) when it fits there whole; else above it when it fits there whole.
 * When it fits on neither side it keeps its whole height rather than scroll — it slides along
 * the window from the roomier side until it is inside the gutters, over the field if need be —
 * and only a popup taller than the whole window is capped (its body scrolls). Left-aligned with
 * the field; everything kept inside the window by the gutter.
 */
export function placePopover(
  anchor: Box,
  size: { width: number; height: number },
  viewport: { width: number; height: number }
): PopoverPlacement {
  const below = viewport.height - POPOVER_GUTTER - (anchor.bottom + POPOVER_GAP);
  const above = anchor.top - POPOVER_GAP - POPOVER_GUTTER;
  const tallest = Math.max(0, viewport.height - 2 * POPOVER_GUTTER);
  let side: PopoverPlacement['side'];
  let maxHeight: number;
  if (size.height <= below) {
    side = 'below';
    maxHeight = below;
  } else if (size.height <= above) {
    side = 'above';
    maxHeight = above;
  } else {
    // Fits on neither side: the whole popup, as close to the roomier side as the window allows.
    side = below >= above ? 'below' : 'above';
    maxHeight = tallest;
  }
  maxHeight = Math.min(maxHeight, tallest);
  const height = Math.min(size.height, maxHeight);
  const wanted = side === 'below' ? anchor.bottom + POPOVER_GAP : anchor.top - POPOVER_GAP - height;
  // Slid inside the window (a popup too tall for its side, a field scrolled out of the window).
  const top = Math.max(
    POPOVER_GUTTER,
    Math.min(wanted, viewport.height - POPOVER_GUTTER - height)
  );
  const width = Math.min(size.width, viewport.width - 2 * POPOVER_GUTTER);
  const left = Math.max(
    POPOVER_GUTTER,
    Math.min(anchor.left, viewport.width - POPOVER_GUTTER - width)
  );
  return { top, left, maxHeight, side };
}

/** The element the page scrolls in around `el` (the app scrolls inside .page-pane, not the body). */
function scrollingAncestor(el: HTMLElement | null): HTMLElement | null {
  for (let node = el?.parentElement ?? null; node && node !== document.body; node = node.parentElement) {
    const { overflowY } = window.getComputedStyle(node);
    if (/(auto|scroll)/.test(overflowY) && node.scrollHeight > node.clientHeight) return node;
  }
  return (document.scrollingElement as HTMLElement | null) ?? null;
}

/** Keep the popup's own clicks and keys from reaching the page around the field (React tree). */
const stop = (e: SyntheticEvent) => e.stopPropagation();

export default function PickerPopover({
  open,
  title,
  onCancel,
  onApply,
  applyLabel = 'Apply',
  applyDisabled = false,
  returnFocusRef,
  initialFocusRef,
  headerAction,
  children,
}: PickerPopoverProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const cancel = useLatest(onCancel);

  const place = useCallback(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const viewport = {
      width: document.documentElement.clientWidth || window.innerWidth,
      height: window.innerHeight || document.documentElement.clientHeight,
    };
    // Measure the natural size, unclamped.
    panel.style.maxHeight = 'none';
    const size = panel.getBoundingClientRect();
    const field = returnFocusRef?.current;
    const anchor: Box =
      field && field.isConnected
        ? field.getBoundingClientRect()
        : // No field to anchor to: centred near the top.
          { top: POPOVER_GUTTER, bottom: POPOVER_GUTTER, left: (viewport.width - size.width) / 2 };
    const p = placePopover(anchor, size, viewport);
    panel.style.top = `${p.top}px`;
    panel.style.left = `${p.left}px`;
    panel.style.maxHeight = `${p.maxHeight}px`;
    panel.dataset.side = p.side;
  }, [returnFocusRef]);

  // Placed before every paint: the content changes size as the picker changes (a sixth week, a
  // message under a field).
  useLayoutEffect(() => {
    if (open) place();
  });

  // Follow the field: window resizes, any scroll outside the popup, late size changes.
  useEffect(() => {
    if (!open) return undefined;
    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        place();
      });
    };
    const onScroll = (e: Event) => {
      if (e.target instanceof Node && panelRef.current?.contains(e.target)) return;
      schedule();
    };
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', onScroll, true);
    const observer =
      typeof ResizeObserver === 'function' && panelRef.current ? new ResizeObserver(schedule) : null;
    if (observer && panelRef.current) observer.observe(panelRef.current);
    return () => {
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', onScroll, true);
      observer?.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    const panel = panelRef.current;
    const opener =
      returnFocusRef?.current ??
      (document.activeElement instanceof HTMLElement ? document.activeElement : null);

    // Focus that lands outside (a screen reader's virtual cursor, a stray script) comes back.
    const onFocusIn = (e: FocusEvent) => {
      if (panel && e.target instanceof Node && !panel.contains(e.target)) {
        (focusableIn(panel)[0] ?? panel).focus({ preventScroll: true });
      }
    };
    document.addEventListener('focusin', onFocusIn);
    const first = initialFocusRef?.current ?? panel;
    first?.focus({ preventScroll: true });
    // Desktop keyboard users type: a text field opens with its text selected.
    if (first instanceof HTMLInputElement) first.select();
    // In a squeezed popup the first field may sit below the fold of the body: scroll the body
    // (only the body — the page must not jump) to show it.
    const body = panel?.querySelector<HTMLElement>('.picker-pop__body');
    if (body && first && first !== panel && body.contains(first)) {
      const view = body.getBoundingClientRect();
      const box = first.getBoundingClientRect();
      if (box.bottom > view.bottom) body.scrollTop += box.bottom - view.bottom + 8;
      else if (box.top < view.top) body.scrollTop -= view.top - box.top + 8;
    }

    return () => {
      document.removeEventListener('focusin', onFocusIn);
      opener?.focus?.({ preventScroll: true });
    };
    // Runs once per opening; the refs are read at that moment on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEscapeKey(open, () => cancel.current());

  if (!open || typeof document === 'undefined') return null;

  // The wheel over the click-catcher still scrolls the page around the field.
  const scrollPage = (e: ReactWheelEvent<HTMLDivElement>) => {
    const target = scrollingAncestor(returnFocusRef?.current ?? null);
    if (!target || typeof target.scrollBy !== 'function') return;
    const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? target.clientHeight : 1;
    target.scrollBy({ left: e.deltaX * unit, top: e.deltaY * unit });
  };

  return createPortal(
    <div
      className="picker-pop-root"
      onClick={stop}
      onMouseDown={stop}
      onPointerDown={stop}
      onKeyDown={stop}
    >
      <div
        className="picker-pop-catcher"
        data-testid="picker-popover-catcher"
        onClick={onCancel}
        onWheel={scrollPage}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="picker-pop"
        onKeyDown={(e) => keepTabInside(e, panelRef.current)}
      >
        <div className="picker-pop__header">
          <h2 id={titleId} className="picker-pop__title">
            {title}
          </h2>
          {headerAction}
          <button
            type="button"
            className="picker-pop__close"
            onClick={onCancel}
            aria-label="Close without changing"
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <div className="picker-pop__body">{children}</div>
        <div className="picker-pop__footer">
          <button type="button" className="picker-pop__cancel" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="picker-pop__apply"
            onClick={onApply}
            disabled={applyDisabled}
          >
            {applyLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
